import type { HTMLAttributes, ReactNode } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** 可点击卡片（例如看板上的创意卡片） */
  interactive?: boolean;
  selected?: boolean;
  tone?: 'default' | 'accent' | 'warning' | 'critical';
}

const CARD_TONES: Record<NonNullable<CardProps['tone']>, string> = {
  default: 'border-line',
  accent: 'border-accent/60',
  warning: 'border-warning/60',
  critical: 'border-critical/70',
};

export function Card({
  children,
  interactive = false,
  selected = false,
  tone = 'default',
  className = '',
  ...rest
}: CardProps) {
  return (
    <div
      className={[
        'rounded-lg border bg-surface-1 transition-colors',
        CARD_TONES[tone],
        selected ? 'ring-2 ring-accent/50' : '',
        interactive
          ? 'cursor-pointer hover:border-line-strong hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
          : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line px-3.5 py-2.5">
      <div className="min-w-0">{children}</div>
      {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

export function CardTitle({ children, subtitle }: { children: ReactNode; subtitle?: ReactNode }) {
  return (
    <>
      <h3 className="truncate text-sm font-semibold text-fg">{children}</h3>
      {subtitle ? <p className="mt-0.5 truncate text-xs text-fg-muted">{subtitle}</p> : null}
    </>
  );
}

export function CardBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={['px-3.5 py-3', className].join(' ')}>{children}</div>;
}

/** 数值型指标块。实时页用 `size="lg"` 渲染大数字。 */
export function Stat({
  label,
  value,
  unit,
  tone = 'neutral',
  size = 'md',
  hint,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  tone?: 'neutral' | 'ok' | 'info' | 'caution' | 'warning' | 'critical';
  size?: 'sm' | 'md' | 'lg';
  hint?: ReactNode;
}) {
  const toneClass =
    tone === 'neutral'
      ? 'text-fg'
      : tone === 'ok'
        ? 'text-ok'
        : tone === 'info'
          ? 'text-info'
          : tone === 'caution'
            ? 'text-caution'
            : tone === 'warning'
              ? 'text-warning'
              : 'text-critical';
  const valueSize =
    size === 'lg' ? 'text-4xl leading-none font-semibold' : size === 'sm' ? 'text-base' : 'text-2xl leading-tight font-semibold';
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-medium tracking-wide text-fg-muted uppercase">{label}</span>
      <span className={['tabular', valueSize, toneClass].join(' ')}>
        {value}
        {unit ? <span className="ml-1 text-xs font-normal text-fg-subtle">{unit}</span> : null}
      </span>
      {hint ? <span className="text-[11px] text-fg-subtle">{hint}</span> : null}
    </div>
  );
}

export function KeyValue({ items, columns = 1 }: { items: { label: ReactNode; value: ReactNode }[]; columns?: 1 | 2 | 3 }) {
  const cols = columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-2' : 'grid-cols-3';
  return (
    <dl className={['grid gap-x-3 gap-y-1.5', cols].join(' ')}>
      {items.map((item, index) => (
        <div key={index} className="flex items-baseline justify-between gap-2 border-b border-line/60 pb-1">
          <dt className="shrink-0 text-xs text-fg-muted">{item.label}</dt>
          <dd className="tabular truncate text-xs font-medium text-fg">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="加载中"
      className={[
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      ].join(' ')}
    />
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      {icon ? <div className="text-fg-subtle">{icon}</div> : null}
      <p className="text-sm font-medium text-fg">{title}</p>
      {description ? <p className="max-w-md text-xs text-fg-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
